// src/Components/Modules/HR/AttendancePunch.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Snackbar,
  Skeleton,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FaFingerprint,
  FaSignOutAlt,
  FaClock,
  FaMapMarkerAlt,
  FaGlobe,
  FaCheckCircle,
  FaExclamationCircle,
  FaUserClock,
  FaCalendarDay,
  FaSync,
  FaInfoCircle,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme, status }) => {
  const colors = {
    present: { bg: "#e8f5e9", color: "#2e7d32", icon: "#4caf50" },
    late: { bg: "#fff3e0", color: "#ef6c00", icon: "#ff9800" },
    absent: { bg: "#ffebee", color: "#c62828", icon: "#f44336" },
    total: { bg: "#e3f2fd", color: "#1565c0", icon: "#2196f3" },
  };
  
  const palette = colors[status] || colors.total;
  
  return {
    background: palette.bg,
    borderRadius: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
  };
});

const PunchButton = styled(Button)(({ theme, variant }) => ({
  padding: theme.spacing(2, 4),
  fontSize: "1.1rem",
  fontWeight: 700,
  borderRadius: theme.spacing(2),
  textTransform: "none",
  transition: "all 0.3s ease",
  ...(variant === "in" && {
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#1b5e20",
    "&:hover": {
      background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
      boxShadow: "0 0 20px rgba(67, 233, 123, 0.4)",
    },
  }),
  ...(variant === "out" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 0 20px rgba(250, 112, 154, 0.4)",
    },
  }),
}));

const ClockDisplay = styled(Box)(({ theme }) => ({
  background: "rgba(255,255,255,0.15)",
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  textAlign: "center",
  border: "1px solid rgba(255,255,255,0.2)",
}));

const AttendancePunch = () => {
  const [punching, setPunching] = useState(false);
  const [summary, setSummary] = useState({});
  const [loc, setLoc] = useState(null);
  const [method, setMethod] = useState("web");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastPunch, setLastPunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [todayHistory, setTodayHistory] = useState([]); // Initialize as empty array

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchData();
    getLocation();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const summaryRes = await AxiosAuthInstance.get("hr/attendance/summary");
      setSummary(summaryRes.data);
      
      // Try to fetch history, but don't fail if endpoint doesn't exist
      try {
        const historyRes = await AxiosAuthInstance.get("hr/attendance/today-history");
        // Ensure we always set an array, even if API returns unexpected data
        const historyData = Array.isArray(historyRes.data) 
          ? historyRes.data 
          : Array.isArray(historyRes.data?.data)
          ? historyRes.data.data
          : [];
        setTodayHistory(historyData);
        
        if (historyData.length > 0) {
          setLastPunch(historyData[historyData.length - 1]);
        }
      } catch (err) {
        // History endpoint might not exist, that's okay
        setTodayHistory([]);
      }
    } catch (error) {
      showSnackbar("Failed to load attendance data", "error");
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLoc({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Geolocation error:", error);
        }
      );
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const punch = async (type) => {
    const result = await Swal.fire({
      title: `Confirm Punch ${type.toUpperCase()}`,
      text: `Are you sure you want to punch ${type}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: type === "in" ? "#43e97b" : "#fa709a",
      cancelButtonColor: "#757575",
      confirmButtonText: `Yes, Punch ${type}`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setPunching(true);
      try {
        const payload = {
          type,
          method,
          latitude: loc?.lat,
          longitude: loc?.lng,
        };
        
        await AxiosAuthInstance.post("hr/attendance/punch", payload);
        
        showSnackbar(`Punch ${type} successful!`, "success");
        await fetchData();
        
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `You have successfully punched ${type}`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        const errorMsg = err.response?.data?.message || `Punch ${type} failed`;
        showSnackbar(errorMsg, "error");
        Swal.fire("Error", errorMsg, "error");
      } finally {
        setPunching(false);
      }
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = () => {
    if (!lastPunch) return "default";
    return lastPunch.type === "in" ? "success" : "error";
  };

  const getStatusText = () => {
    if (!lastPunch) return "No punch yet";
    return lastPunch.type === "in" ? "Checked In" : "Checked Out";
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumb title="Attendance Punch" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Attendance Punch" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "space-between" }, alignItems: "center", flexWrap: "wrap", gap: 2, textAlign: { xs: "center", sm: "left" } }}>
          <Box  
            sx={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" }
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserClock style={{ marginRight: 10, verticalAlign: "middle" }} />
              Attendance Punch
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track your daily attendance with ease
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchData}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="present" elevation={0}>
            <CardContent>
              <FaCheckCircle size={32} color="#4caf50" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#2e7d32">
                {summary.present || 0}
              </Typography>
              <Typography variant="body2" color="#2e7d32" fontWeight={600}>
                Present
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="late" elevation={0}>
            <CardContent>
              <FaExclamationCircle size={32} color="#ff9800" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#ef6c00">
                {summary.late || 0}
              </Typography>
              <Typography variant="body2" color="#ef6c00" fontWeight={600}>
                Late
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="absent" elevation={0}>
            <CardContent>
              <FaExclamationCircle size={32} color="#f44336" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#c62828">
                {summary.absent || 0}
              </Typography>
              <Typography variant="body2" color="#c62828" fontWeight={600}>
                Absent
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="total" elevation={0}>
            <CardContent>
              <FaCalendarDay size={32} color="#2196f3" style={{ marginBottom: 10 }} />
              <Typography variant="h3" fontWeight={700} color="#1565c0">
                {summary.total || 0}
              </Typography>
              <Typography variant="body2" color="#1565c0" fontWeight={600}>
                Total Employees
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Main Punch Card */}
      <Paper elevation={3} sx={{ borderRadius: 3, p: 4, mb: 3 }}>
        <Grid container spacing={4}>
          {/* Clock Display */}
          <Grid item xs={12} md={6}>
            <ClockDisplay>
              <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                <FaClock style={{ marginRight: 8, verticalAlign: "middle" }} />
                Current Time
              </Typography>
              <Typography variant="h2" fontWeight={700} sx={{ my: 2 }}>
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {formatDate(currentTime)}
              </Typography>
              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }} />
              <Box>
                <Chip
                  icon={getStatusColor() === "success" ? <FaCheckCircle /> : <FaSignOutAlt />}
                  label={getStatusText()}
                  color={getStatusColor()}
                  sx={{ fontWeight: 600, px: 2, py: 1 }}
                />
                {lastPunch && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                    Last: {new Date(lastPunch.timestamp).toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            </ClockDisplay>

            {/* Location Info */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Punch Method
              </Typography>
              <ToggleButtonGroup
                value={method}
                exclusive
                onChange={(e, val) => val && setMethod(val)}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="web">
                  <FaGlobe style={{ marginRight: 6 }} />
                  Web
                </ToggleButton>
                <ToggleButton value="gps" disabled={!loc}>
                  <FaMapMarkerAlt style={{ marginRight: 6 }} />
                  GPS
                </ToggleButton>
              </ToggleButtonGroup>
              
              {loc && (
                <Alert 
                  icon={<FaMapMarkerAlt />} 
                  severity="success" 
                  sx={{ mb: 2 }}
                >
                  Location: {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}
                </Alert>
              )}
              
              {!loc && method === "gps" && (
                <Alert icon={<FaInfoCircle />} severity="warning">
                  GPS location not available. Using web punch.
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Punch Buttons */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Punch Your Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Click the button below to record your attendance
              </Typography>

              <Stack spacing={3} direction={{ xs: "column", sm: "row" }} justifyContent="center">
                <PunchButton
                  variant="in"
                  size="large"
                  disabled={punching}
                  onClick={() => punch("in")}
                  startIcon={punching ? null : <FaFingerprint />}
                >
                  {punching ? "Processing..." : "PUNCH IN"}
                </PunchButton>

                <PunchButton
                  variant="out"
                  size="large"
                  disabled={punching}
                  onClick={() => punch("out")}
                  startIcon={punching ? null : <FaSignOutAlt />}
                >
                  {punching ? "Processing..." : "PUNCH OUT"}
                </PunchButton>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>
                <FaInfoCircle style={{ verticalAlign: "middle", marginRight: 4 }} />
                Make sure you're at the correct location before punching
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Today's History - FIXED: Added Array.isArray check */}
      {Array.isArray(todayHistory) && todayHistory.length > 0 && (
        <Paper elevation={2} sx={{ borderRadius: 2, p: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            <FaClock style={{ marginRight: 8, verticalAlign: "middle" }} />
            Today's Punch History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            {todayHistory.map((punch, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  bgcolor: punch.type === "in" ? "#e8f5e9" : "#ffebee",
                  borderRadius: 1,
                  borderLeft: `4px solid ${punch.type === "in" ? "#4caf50" : "#f44336"}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {punch.type === "in" ? (
                    <FaCheckCircle color="#4caf50" />
                  ) : (
                    <FaSignOutAlt color="#f44336" />
                  )}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {punch.type === "in" ? "Check In" : "Check Out"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {punch.method === "gps" ? "GPS" : "Web"}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {new Date(punch.timestamp).toLocaleTimeString()}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AttendancePunch;